'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import styled, { keyframes } from 'styled-components';

import { Download, Menu, MenuClose, NavChevronDown, NavChevronUp } from '@/components/icons';
import data from '@/data/shared/header.json';
import { useMediaMatch } from '@/hooks/useMediaMatch';
import { TypographyStyles, breakpoints, colors, media } from '@/styles/shared';
import * as Accordion from '@radix-ui/react-accordion';
import * as Nav from '@radix-ui/react-navigation-menu';

const slideDown = keyframes`
    from {
		max-height: 0px;
	}
	to {
		max-height: var(--radix-accordion-content-height);
	}
`;

const slideUp = keyframes`
    from {
		max-height: var(--radix-accordion-content-height);
	}
	to {
		max-height: 0px;
	}
`;

const S = {
  Root: styled.div`
    background-color: ${colors.greyNav};
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  Nav: styled(Nav.Root)`
    display: flex;
    height: 58px;
    justify-content: space-between;
    margin: 0 auto;
    max-width: 1920px;
    padding: 0 16px;
    width: 100%;

    div {
      align-items: center;
      display: flex;
      gap: 16px;

      svg {
        height: 18px;
        width: auto;
      }
    }

    .mobile-icons {
      display: flex;
      gap: 0;

      button {
        align-items: center;
        display: flex;
        height: 48px;
        justify-content: center;
        width: 48px;
      }

      svg {
        height: 18px;
        width: auto;
      }

      @media screen and (width >= 1240px) {
        display: none;
      }
    }

    @media ${media.greaterThan('md')} {
      padding: 0 32px;
    }

    @media screen and (width >= 1240px) {
      justify-content: space-between;
      padding: 0 48px;
    }

    @media ${media.greaterThan('xx')} {
      height: 64px;
      padding: 0 80px;
    }

    @media ${media.greaterThan('xxl')} {
      padding: 0 112px;
    }

    .left-desktop-links {
      display: flex;
      gap: 40px;

      span {
        color: ${colors.greyNavPopup};
        display: none;

        @media screen and (width >= 1240px) {
          display: flex;
        }
      }
    }

    .right-desktop-links {
      ${TypographyStyles.Body.Small};
      color: ${colors.evergreen};
      display: none;
      gap: 16px;

      a {
        border-bottom: 3px solid transparent;
        padding: 20px 16px 16px 12px;

        &:hover,
        &:focus {
          border-bottom: 3px solid ${colors.evergreen};
        }

        @media ${media.greaterThan('xx')} {
          padding: 23px 16px 16px 12px;
        }
      }

      @media screen and (width >= 1240px) {
        display: flex;
      }

      @media ${media.greaterThan('xx')} {
        ${TypographyStyles.Body.Regular};
      }
    }
  `,
  SustainabilityLink: styled(Link)`
    ${TypographyStyles.Body.Small};
    align-items: center;
    border-bottom: 3px solid transparent;
    color: ${colors.evergreen};
    display: flex;
    font-weight: 500;
    height: 100%;

    &:hover {
      border-bottom: 3px solid ${colors.evergreen};
    }

    @media ${media.greaterThan('xx')} {
      ${TypographyStyles.Body.Large};
      font-weight: 500;
    }
  `,
  NavList: styled(Nav.List)`
    ${TypographyStyles.Body.Small};
    color: ${colors.evergreen};
    display: none;

    @media screen and (width >= 1240px) {
      align-items: center;
      display: flex;
      gap: 16px;
      height: 100%;

      li {
        align-items: center;
        display: flex;
        height: 100%;
      }
    }

    @media ${media.greaterThan('xx')} {
      ${TypographyStyles.Body.Regular};
    }
  `,
  NavItem: styled(Nav.Item)`
    position: relative;

    &.no-popup {
      border-bottom: 3px solid transparent;
      display: flex;
      gap: 4px;
      height: 58px;

      &:focus-within,
      &:hover {
        border-bottom: 3px solid ${colors.evergreen};
      }

      a {
        padding: 19px 16px 16px 12px;
      }

      @media ${media.greaterThan('xx')} {
        height: 64px;

        a {
          padding: 22px 16px 16px 12px;
        }
      }
    }
  `,
  NavTrigger: styled(Nav.Trigger)`
    border-bottom: 3px solid transparent;
    display: flex;
    gap: 4px;
    height: 58px;
    padding: 19px 0 16px 12px;
    position: relative;

    .caret {
      display: flex;
      height: 24px;
      justify-content: center;
      width: 24px;

      svg {
        height: 6px;
      }
    }

    .expand {
      display: flex;
    }

    .collapse {
      display: none;
    }

    &[data-state='open'] {
      .expand {
        display: none;
      }

      .collapse {
        display: flex;
      }

      border-bottom: 3px solid ${colors.evergreen};

      &::after {
        background-color: ${colors.day};
        border-left: 1px solid ${colors.greyNavPopup};
        border-top: 1px solid ${colors.greyNavPopup};
        content: '';
        height: 8px;
        left: 50%;
        position: absolute;
        top: 77px;
        transform: rotate(45deg) translate(-50%, -50%);
        width: 8px;
        z-index: 3;
      }
    }

    @media ${media.greaterThan('xx')} {
      height: 64px;
      padding: 22px 0 16px 12px;
    }
  `,
  NavContent: styled(Nav.Content)`
    ${TypographyStyles.CallToActionAndNav.CTA};
    background-color: ${colors.day};
    border: 1px solid ${colors.greyNavPopup};
    border-radius: 8px;
    color: ${colors.evergreen};
    display: flex;
    flex-direction: column;
    font-size: 14px;
    gap: 12px;
    left: 50%;
    padding: 24px 32px;
    position: absolute;
    top: 75px;
    transform: translate(-50%, 0);
    width: 273px;
    z-index: 2;

    a {
      border-bottom: 2px solid transparent;
      margin-inline-end: auto;
      padding: 0;
      width: fit-content;

      &:hover {
        border-bottom: 2px solid ${colors.evergreen};
        text-transform: underline;
      }
    }

    @media ${media.greaterThan('xx')} {
      font-size: 16px;
      gap: 16px;
    }
  `,
  AccordionMenu: styled(Accordion.Root)<{ $isOpen: boolean }>`
    background-color: ${colors.day};
    height: ${({ $isOpen }) => ($isOpen ? '100vh' : 0)};
    margin: 0 auto;
    max-height: ${({ $isOpen }) => ($isOpen ? '2000px' : 0)};
    overflow: scroll;
    transition: max-height 0.3s ease-in-out;
    width: 100%;

    .evergreen-bg {
      background-color: ${colors.evergreen};
    }

    .row-item {
      display: flex;
      padding: 16px;
      width: 100%;

      &.download {
        background-color: ${colors.day};
        display: flex;
        flex-direction: column;
        gap: 12px;

        @media screen and (width >= 1240px) {
          display: none;
        }

        > a {
          width: 225px;
        }
      }

      @media screen and (min-width: ${breakpoints.md}rem) {
        padding: 16px 32px;
      }
    }

    @media screen and (width >= 1240px) {
      max-height: 0;
    }
  `,
  AccordionTrigger: styled(Accordion.Trigger)`
    ${TypographyStyles.Body.Regular};
    background-color: ${colors.day};
    color: ${colors.evergreen};
    display: flex;
    justify-content: space-between;
    line-height: 20px;
    padding: 16px 32px 16px 16px;
    width: 100%;

    &[data-state='open'] {
      .expand {
        display: none;
      }

      background-color: ${colors.evergreen};
      color: ${colors.day};
    }

    &[data-state='closed'] {
      .collapse {
        display: none;
      }
    }

    &:hover {
      text-decoration: underline;
    }

    @media screen and (min-width: ${breakpoints.md}rem) {
      padding: 16px 48px 16px 32px;
    }
  `,
  AccordionContent: styled(Accordion.Content)`
    background-color: ${colors.day};
    margin: 0 0 0 16px;
    overflow: hidden;

    &[data-state='open'] {
      animation: ${slideDown} 0.3s ease-out;
    }

    &[data-state='closed'] {
      animation: ${slideUp} 0.3s ease-out;
    }
  `,
  ContentContainer: styled.div`
    background-color: ${colors.evergreen};
  `,
  AccordionLink: styled(Link)`
    ${TypographyStyles.Body.Regular};
    background-color: ${colors.day};
    color: ${colors.evergreen};
    display: flex;
    line-height: 20px;
    padding: 16px;

    &:hover {
      text-decoration: underline;
    }

    @media screen and (min-width: ${breakpoints.md}rem) {
      padding: 16px 32px;
    }
  `,

  Button: styled.a`
    ${TypographyStyles.Labels.Regular}
    align-items: center;
    background-color: ${colors.urgencyGreen};
    border-radius: 200px;
    color: ${colors.evergreen};
    display: flex;
    font-weight: 600;
    gap: 12px;
    height: 34px;
    justify-content: center;
    padding: 8px 16px;
  `,
  DownloadButton: styled.a`
    ${TypographyStyles.Labels.Regular};
    background-color: ${colors.day};
    border: 2px solid ${colors.evergreen};
    border-radius: 200px;
    color: ${colors.evergreen};
    display: flex;
    font-weight: 600;
    gap: 12px;
    padding: 8px 16px;

    svg {
      height: 20px;
      width: 20px;
    }
  `,
};

const NavMenu = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const isDesktop = useMediaMatch(media.greaterThan('xl'));

  const onMenuClose = () => {
    setMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  const onMenuOpen = () => {
    setMenuOpen(true);
    document.body.style.overflow = 'hidden';
  };

  useEffect(() => {
    setMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, [pathname]);

  useEffect(() => {
    if (isDesktop) document.body.style.overflow = 'auto';
  }, [isDesktop]);

  return (
    <S.Root>
      <S.Nav>
        <div className="left-desktop-links">
          <S.SustainabilityLink href={'/sustainability'}>Sustainability</S.SustainabilityLink>
          <span>|</span>
          <S.NavList>
            {data.navMenu.slice(0, -1).map((menuItem, index) => {
              const hasDropdown = menuItem.links.length > 1;
              return (
                <S.NavItem key={`nav-item-${index}`} className={!hasDropdown ? 'no-popup' : ''}>
                  {hasDropdown ? (
                    <>
                      <S.NavTrigger>
                        {menuItem.title}
                        <div className="expand caret">
                          <NavChevronDown color={colors.evergreen} />
                        </div>
                        <div className="collapse caret">
                          <NavChevronUp color={colors.evergreen} />
                        </div>
                      </S.NavTrigger>
                      <S.NavContent>
                        {menuItem.links.map((link, linkIdx) => (
                          <Link key={`link-${index}-${linkIdx}`} href={link.href}>
                            {link.text}
                          </Link>
                        ))}
                      </S.NavContent>
                    </>
                  ) : (
                    <Nav.Link href={menuItem.links[0].href}>{menuItem.links[0].text}</Nav.Link>
                  )}
                </S.NavItem>
              );
            })}
          </S.NavList>
        </div>
        <div className="right-desktop-links">
          <Link href={data.navMenu[3].links[0].href}>{data.navMenu[3].links[0].text}</Link>
        </div>
        <div className="mobile-icons">
          {!menuOpen && (
            <button aria-label="Open menu" className="open-menu" onClick={onMenuOpen}>
              <Menu color={colors.evergreen} />
            </button>
          )}
          {menuOpen && (
            <button aria-label="Close menu" className="close-menu" onClick={onMenuClose}>
              <MenuClose color={colors.evergreen} />
            </button>
          )}
        </div>
      </S.Nav>
      <S.AccordionMenu type="single" collapsible $isOpen={menuOpen}>
        {data.navMenu.map((item) =>
          item.links.length === 1 ? (
            <S.AccordionLink key={item.value} href={item.links[0].href}>
              {item.links[0].text}
            </S.AccordionLink>
          ) : (
            <Accordion.Item key={item.value} value={item.value}>
              <S.AccordionTrigger>
                {item.title}
                <div className="expand">
                  <NavChevronDown color={colors.evergreen} />
                </div>
                <div className="collapse">
                  <NavChevronUp color={colors.day} />
                </div>
              </S.AccordionTrigger>
              <S.ContentContainer>
                <S.AccordionContent>
                  {item.links.map((link, index) => (
                    <S.AccordionLink key={index} href={link.href}>
                      {link.text}
                    </S.AccordionLink>
                  ))}
                </S.AccordionContent>
              </S.ContentContainer>
            </Accordion.Item>
          )
        )}
        <div className="row-item download">
          <S.Button href={data.skinnyBanner.cta.url}>
            <span>{data.skinnyBanner.cta.text}</span>
          </S.Button>

          <S.DownloadButton href={data.skinnyBanner.cta2.url} target="_blank">
            <span>{data.skinnyBanner.cta2.text}</span>
            <Download color={colors.evergreen} />
          </S.DownloadButton>
        </div>
      </S.AccordionMenu>
    </S.Root>
  );
};
export default NavMenu;
